import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Navigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Wallet, DollarSign, Plus, Pencil, Trash2, X } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";

// Financials — DRIVN has no subcontractors, so this is purely customer-side:
// what each customer was billed, what they've paid, and what's outstanding,
// plus the expected margin (sell − carrier buy cost). "Received" combines the
// manual payment log below with any invoice explicitly marked Paid on the
// Invoicing tab (see /financials/summary), so it stays correct even when
// staff only ever use the invoice status dropdown.
export default function Financials() {
  const { user } = useAuth();
  const [summary, setSummary] = useState({ customers: [], totals: {} });
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preset, setPreset] = useState(null);     // { custId } → open prefilled

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get("/financials/summary"),
        api.get("/financials/payments"),
      ]);
      setSummary(s.data || { customers: [], totals: {} });
      setPayments(Array.isArray(p.data) ? p.data : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const totals = summary.totals || {};
  const custs = summary.customers || [];
  const fmt = (n) => `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const removePayment = async (id) => {
    if (!window.confirm("Delete this payment record?")) return;
    try {
      await api.delete(`/financials/payments/${id}`);
      toast.success("Payment removed");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (user && user.role !== "admin") return <Navigate to="/dashboard/job-status" replace />;

  return (
    <div data-testid="financials-page">
      <div className="flex justify-between items-center mb-8 flex-wrap gap-3">
        <div>
          <h1 className="brand-title text-2xl sm:text-3xl flex items-center gap-3">
            <Wallet className="text-emerald-400" size={26} /> Financials
          </h1>
          <p className="text-white/60 text-sm mt-1">Per-customer billed vs received · editable payment log</p>
        </div>
        <button onClick={() => setAdding(true)} className="pill-btn flex items-center gap-2 !text-sm" data-testid="record-payment-btn">
          <Plus size={16} /> RECORD PAYMENT
        </button>
      </div>

      {/* Headline totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <SummaryCard label="Received Till Now" value={fmt(totals.received)} color="#22c55e" testid="received-total" sublabel="From logged receipts" />
        <SummaryCard label="Total Billed (inc GST)" value={fmt(totals.billed)} color="#19e6c8" testid="billed-total" />
        <SummaryCard label="Outstanding" value={fmt(totals.outstanding)} color="#f59e0b" testid="outstanding-total" sublabel="Billed − received" />
        <SummaryCard label="Expected Margin" value={fmt(totals.total_margin)} color="#a78bfa" testid="margin-total" sublabel="Sell − carrier buy cost" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <SummaryCard label="Carrier Cost (Buy)" value={fmt(totals.buy_total)} color="#ec4899" testid="buy-total" />
        <SummaryCard label="Loads" value={totals.job_count ?? 0} color="#06b6d4" testid="loads-total" plain />
      </div>

      {/* By customer */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <h2 className="font-semibold text-white/90">By Customer</h2>
          <span className="text-xs text-white/40">{custs.length} customer{custs.length !== 1 ? "s" : ""}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.04] text-xs uppercase tracking-wider text-white/50">
              <tr>
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-right">Jobs</th>
                <th className="px-4 py-3 text-right">Billed</th>
                <th className="px-4 py-3 text-right">Received</th>
                <th className="px-4 py-3 text-right">Outstanding</th>
                <th className="px-4 py-3 text-right">Margin</th>
                <th className="px-4 py-3 text-right">Record</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-6 text-center text-white/40">Loading…</td></tr>}
              {!loading && custs.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-white/40">No customer activity yet.</td></tr>
              )}
              {custs.map((c) => (
                <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.04]" data-testid={`cust-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-white">{c.company || c.name}</div>
                    {c.company && c.name && c.company !== c.name && <div className="text-xs text-white/50">{c.name}</div>}
                  </td>
                  <td className="px-4 py-3 text-right text-white/70">{c.job_count}</td>
                  <td className="px-4 py-3 text-right font-mono text-sky-300">{fmt(c.billed)}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-300">{fmt(c.received)}</td>
                  <td className={`px-4 py-3 text-right font-mono font-bold ${Number(c.outstanding) > 0 ? "text-amber-300" : "text-emerald-400"}`}>
                    {fmt(c.outstanding)}
                  </td>
                  <td className={`px-4 py-3 text-right font-mono ${Number(c.margin) >= 0 ? "text-emerald-300" : "text-red-300"}`}>{fmt(c.margin)}</td>
                  <td className="px-4 py-3 text-right">
                    {c.id !== "_unassigned" && (
                      <button onClick={() => setPreset({ custId: c.id })} className="text-white/50 hover:text-emerald-400"
                        title="Record money received from this customer" data-testid={`record-customer-${c.id}`}>
                        <Pencil size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment log */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden mt-8">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <h2 className="font-semibold text-white/90">Payment Log</h2>
          <span className="text-xs text-white/40">{payments.length} entries</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.04] text-xs uppercase tracking-wider text-white/50">
              <tr>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-left">Note</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-white/40">No payments recorded yet.</td></tr>
              )}
              {payments.map((p) => (
                <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-white/80">{(p.paid_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-emerald-300 text-xs">{p.customer_name || "Customer"}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold">{fmt(p.amount)}</td>
                  <td className="px-4 py-3 text-white/60 text-xs">{p.note || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setEditing(p)} className="text-white/50 hover:text-[#19e6c8] mr-2"><Pencil size={14} /></button>
                    <button onClick={() => removePayment(p.id)} className="text-white/50 hover:text-red-400"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {(adding || editing || preset) && (
        <PaymentModal
          initial={editing}
          preset={preset}
          customers={custs}
          onClose={() => { setAdding(false); setEditing(null); setPreset(null); }}
          onSaved={() => { setAdding(false); setEditing(null); setPreset(null); load(); }}
        />
      )}
    </div>
  );
}

function SummaryCard({ label, value, color, testid, sublabel, plain }) {
  const str = String(value ?? "");
  const len = str.length;
  const sizeCls = len > 16 ? "text-lg" : len > 13 ? "text-xl" : len > 11 ? "text-2xl" : "text-3xl";
  return (
    <div className="relative rounded-2xl border border-white/10 p-5 min-w-0" style={{ background: `${color}10` }} data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-white/50 truncate">{label}</p>
      <p className={`brand-title ${plain ? "text-3xl" : sizeCls} mt-1 whitespace-nowrap overflow-hidden text-ellipsis leading-tight`} style={{ color }} title={str}>
        {value}
      </p>
      {sublabel && <p className="text-[10px] text-white/35 mt-0.5 truncate">{sublabel}</p>}
    </div>
  );
}

// Record / edit a customer receipt. On create it always posts a customer
// payment (kind=invoice_received); on edit it patches amount/date/note.
function PaymentModal({ initial, preset, customers, onClose, onSaved }) {
  const isEdit = !!initial;
  const [custId, setCustId] = useState(initial?.customer_id || preset?.custId || "");
  const [amount, setAmount] = useState(initial?.amount || "");
  const [paidAt, setPaidAt] = useState((initial?.paid_at || new Date().toISOString()).slice(0, 10));
  const [note, setNote] = useState(initial?.note || "");
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/financials/payments/${initial.id}`, { amount: Number(amount), note, paid_at: new Date(paidAt).toISOString() });
        toast.success("Payment updated");
      } else {
        if (!custId) { toast.error("Pick a customer"); setBusy(false); return; }
        await api.post("/financials/customer-payments", {
          customer_id: custId, amount: Number(amount), paid_at: new Date(paidAt).toISOString(), note,
        });
        toast.success("Payment recorded");
      }
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const selectable = (customers || []).filter((c) => c.id !== "_unassigned");

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-[#0b1430] border border-white/10 rounded-2xl p-6 w-full max-w-md my-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="brand-title text-xl">{isEdit ? "Edit Payment" : "Record Payment"}</h3>
          <button type="button" onClick={onClose} className="text-white/60 hover:text-white"><X size={20} /></button>
        </div>
        <div className="space-y-3">
          {!isEdit && (
            <>
              <label className="block text-white/85 text-sm">Customer *</label>
              <select value={custId} onChange={(e) => setCustId(e.target.value)} className="df-input-rect" required data-testid="payment-customer">
                <option value="" className="bg-[#0b1430]">— Pick customer —</option>
                {selectable.map((c) => (
                  <option key={c.id} value={c.id} className="bg-[#0b1430]">
                    {c.company || c.name} — outstanding ${Number(c.outstanding || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-white/40 -mt-1">Money received from this customer — adds to "Received Till Now".</p>
            </>
          )}
          <label className="block text-white/85 text-sm">Amount (AUD) *</label>
          <div className="relative">
            <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="df-input-rect" style={{ paddingLeft: "2rem" }} required data-testid="payment-amount" />
          </div>
          <label className="block text-white/85 text-sm">Date</label>
          <DatePicker value={paidAt} onChange={setPaidAt} />
          <label className="block text-white/85 text-sm">Note</label>
          <textarea rows="2" value={note} onChange={(e) => setNote(e.target.value)} className="df-input-rect resize-none" />
        </div>
        <div className="flex justify-end mt-5">
          <button type="submit" disabled={busy} className="px-5 py-2.5 rounded-lg text-[#04201c] font-semibold" style={{ background: "linear-gradient(90deg, #19e6c8, #0fbfa6)" }} data-testid="payment-record-btn">
            {busy ? "..." : (isEdit ? "Save" : "Record")}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}
